import React, { useState } from 'react';
import { useDrop } from 'react-dnd';
import { changeDateFormat } from '@services/date/date-format';
import useNotification from '@hooks/notification/useNotification';
import UiButton from '@components/ui/button/UiButton';
import { patchRejectApplicant } from '@services/applicants/applicants-http.service';
import RedPersonXIcon from '@assets/svgs/red-person-x-icon.svg';
import UiTag from '@components/ui/tag/UiTag';
import StateEnum, { StateEnumTag } from '@constants/state.enum';
import SettingsDotsIcon from '@assets/svgs/grey-dots-icon.svg';
import { Modal, Table } from 'antd';
import { StepApplicantElement, StepApplicantsElement } from '@models/applicants';
import { getRejectedApplicants } from '@services/steps/steps-http.service';
import { useTranslation } from 'next-i18next';
import styles from './RejectedCard.module.scss';

type RejectedCardCounter = {
  positionId: string;
  stepTitle: string;
  stepId: string;
  rejectedCounter: number;
  fetchRejectedCounter: () => void;
  fetchApplicants: () => void;
};
const RejectedCard: React.FC<RejectedCardCounter> = ({
  positionId,
  stepTitle,
  rejectedCounter,
  fetchRejectedCounter,
  stepId,
  fetchApplicants,
}) => {
  const { t: translate } = useTranslation('position');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [rejectedApplicants, setRejectedApplicants] = useState<StepApplicantsElement>();

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const fetchRejectedApplicants = async () => {
    try {
      const rejectedApplicantsElem: StepApplicantsElement = await getRejectedApplicants(positionId, stepId);
      await setRejectedApplicants(rejectedApplicantsElem);
    } catch (e: any) {
      generalError();
    }
  };

  const rejectApplicant = async (applicantId: string) => {
    try {
      await patchRejectApplicant(positionId, applicantId, stepId, false);
      await fetchApplicants();
      await fetchRejectedCounter();
      await fetchRejectedApplicants();
    } catch (e: any) {
      generalError();
    }
  };

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: 'card',
      drop: async (card: { id: string; step: string }) => {
        try {
          await patchRejectApplicant(positionId, card.id, card.step, true);
          await fetchApplicants();
          await fetchRejectedCounter();
        } catch (e: any) {
          generalError();
        }
      },
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
      }),
    }),
    [],
  );

  const showModal = async () => {
    await fetchRejectedApplicants();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const hoverColumnState = () => {
    if (isOver) {
      return (
        <div className={styles.hoverRejectedCard} ref={drop}>
          <div className={styles.rejectedIconText}>
            <RedPersonXIcon className={styles.rejectedIcon} />
            <p className={`h6 ${styles.rejectedText}`}>{translate('reject')}</p>
          </div>
          <div className={styles.rejectedCounter}>
            <div className={styles.counter}>
              <UiTag text={rejectedCounter.toString()} type={StateEnumTag.ERROR} />
            </div>
            <UiButton type="ghost" className={styles.dotsCounter} icon={<SettingsDotsIcon />} />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.overRejectedCard} ref={drop}>
        <div className={styles.rejectedIconText}>
          <RedPersonXIcon className={styles.rejectedIcon} />
          <p className={`h6 ${styles.rejectedText}`}>{translate('reject')}</p>
        </div>
        <div className={styles.rejectedCounter}>
          <div className={styles.counter}>
            <UiTag text={rejectedCounter.toString()} type={StateEnumTag.ERROR} />
          </div>
          <UiButton type="ghost" className={styles.dotsCounter} icon={<SettingsDotsIcon />} />
        </div>
      </div>
    );
  };

  const columns = [
    {
      key: 'id',
      title: 'id',
      dataIndex: 'id',
      hidden: true,
    },
    {
      key: 'firstName',
      title: <div className="main-body-text">{translate('full_name')}</div>,
      dataIndex: 'firstName',
      width: 200,
      render: function renderText(text: string, row: StepApplicantElement) {
        return <div className="large-body-text">{`${row.firstName} ${row.lastName}`}</div>;
      },
    },
    {
      key: 'createdDate',
      title: <div className="main-body-text">{translate('created')}</div>,
      dataIndex: 'createdDate',
      render: function renderText(text: Date) {
        return <div className={`small-body-text ${styles.dateField}`}>{changeDateFormat(text)}</div>;
      },
    },
    {
      key: 'actions',
      dataIndex: 'actions',
      title: <div className="main-body-text">{translate('cancel_rejection')}</div>,
      render: function renderText(text: string, row: StepApplicantElement) {
        return (
          <UiButton
            size="small"
            type="primary"
            className={styles.unRejectButton}
            onClick={() => rejectApplicant(row.id.toString())}
          >
            {translate('cancel_rejection')}
          </UiButton>
        );
      },
    },
  ].filter((item) => !item.hidden);

  return canDrop ? (
    <div className={styles.wrapOverRejectedCard}>{hoverColumnState()}</div>
  ) : (
    <>
      <div
        className={styles.wrapRejectedCard}
        ref={drop}
        onClick={() => showModal()}
        aria-hidden="true"
        data-testid="rejected-area"
      >
        <div className={styles.rejectedIconText}>
          <RedPersonXIcon className={styles.rejectedIcon} />
          <p className={`h6 ${styles.rejectedText}`}>{translate('rejected')}</p>
        </div>
        <div className={styles.rejectedCounter}>
          <UiTag text={rejectedCounter.toString()} type={StateEnumTag.ERROR} />
          <UiButton type="ghost" className={styles.dotsCounter} icon={<SettingsDotsIcon />} />
        </div>
      </div>
      <Modal
        title={`${translate('rejected_in_step')}${stepTitle}`}
        open={isModalOpen}
        footer={null}
        onCancel={closeModal}
        className={styles.rejectedModal}
      >
        <Table
          rowClassName={() => {
            return `${styles.tableRow}`;
          }}
          dataSource={rejectedApplicants?.items}
          columns={columns}
          pagination={false}
          scroll={{ y: 400 }}
          className={styles.rejectedCardTable}
        />
      </Modal>
    </>
  );
};

export default RejectedCard;
